import { useEffect, type FC } from "react";

import { useContactPhotoUrl } from "@/shared/hooks/useContactPhotoUrl";
import { cn, getInitials } from "@/shared/lib/utils";
import { formatContactName } from "@/shared/utils/contactUtils";

import type { PersonContact } from "@/shared/types/estateContact";

interface ContactAvatarProps {
  contact: PersonContact;
  className?: string;
}

export const ContactAvatar: FC<ContactAvatarProps> = ({ contact, className }) => {
  const { photoUrl, isLoading, error, refreshPhotoUrl } = useContactPhotoUrl(
    contact.profile_picture ? contact.id : null,
  );

  useEffect(() => {
    const handleContactUpdate = (event: CustomEvent) => {
      if (event.detail.contactId === contact.id) {
        refreshPhotoUrl();
      }
    };

    window.addEventListener("contactUpdated", handleContactUpdate as EventListener);

    return () => {
      window.removeEventListener("contactUpdated", handleContactUpdate as EventListener);
    };
  }, [contact.id, refreshPhotoUrl]);

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex aspect-square size-[32px] leading-none items-center justify-center rounded-full bg-stone-200 animate-pulse",
          className,
        )}
      >
        <div className="w-4 h-4 bg-stone-300 rounded-sm"></div>
      </div>
    );
  }

  if (photoUrl && !error) {
    return (
      <div
        className={cn(
          "flex aspect-square size-[32px] leading-none items-center justify-center rounded-full bg-brand text-white font-semibold text-sm overflow-hidden",
          className,
        )}
      >
        <img
          src={photoUrl}
          alt={`${formatContactName(contact.first_name, contact.last_name)}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = getInitials({
                fullName: formatContactName(contact.first_name, contact.last_name),
              });
            }
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex aspect-square size-[32px] leading-none items-center justify-center rounded-full bg-brand text-white font-semibold text-sm",
        className,
      )}
    >
      {getInitials({
        fullName: formatContactName(contact.first_name, contact.last_name),
      })}
    </div>
  );
};
